import React, { useState, useEffect } from 'react';
import { saveNickname, FIVE_LEAGUE, FL_GAME_MODE, CHAIN_SETTINGS } from '../shared/fiveLeague';
import { createRoom, findRoomByCode, joinRoom, quickMatch } from '../shared/roomTx';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import AdBanner from '../components/AdBanner';
import HowToPlayModal, { hasSeenTutorial } from '../components/HowToPlayModal';
import AuthModal from '../components/AuthModal';
import QuestionSubmitModal from '../components/QuestionSubmitModal';
import { auth, isEmailUser } from '../firebase';
import './Home.css';

// 機能フラグ: アンサーチェーンモードを有効にするか
const ENABLE_CHAIN_MODE = false; // TODO: 本番公開時にtrueに戻す

function Home({ navigate, playerId, nickname, setNickname }) {
  const [mode, setMode] = useState('menu'); // menu, select-mode, create, join, quick
  const [inputNickname, setInputNickname] = useState(nickname);
  const [roomCode, setRoomCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showSubmitQuestion, setShowSubmitQuestion] = useState(false);
  const [, forceUpdate] = useState(0);

  // ゲームモード選択
  const [selectedGameMode, setSelectedGameMode] = useState(FL_GAME_MODE.ONEWORD);
  const [selectedAnswerCount, setSelectedAnswerCount] = useState(CHAIN_SETTINGS.ANSWER_COUNT_5);

  // 初回訪問時に遊び方を表示（登録済みユーザーは除く）
  useEffect(() => {
    if (!hasSeenTutorial() && !isEmailUser()) {
      setShowHowToPlay(true);
    }
  }, []);

  // 認証状態変更時に再レンダリング
  const handleAuthChange = () => {
    forceUpdate(n => n + 1);
  };

  const handleQuickMatch = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      const config = selectedGameMode === FL_GAME_MODE.CHAIN
        ? { answerCount: selectedAnswerCount }
        : {};

      const result = await quickMatch({
        playerId,
        nickname: inputNickname.trim(),
        gameMode: selectedGameMode,
        config,
      });

      if (result.ok) {
        navigate('wait', { roomId: result.roomId });
      } else {
        setError('マッチングに失敗しました');
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      const config = selectedGameMode === FL_GAME_MODE.CHAIN
        ? { answerCount: selectedAnswerCount }
        : {};

      const result = await createRoom({
        hostId: playerId,
        nickname: inputNickname.trim(),
        gameMode: selectedGameMode,
        config,
      });

      if (result.ok) {
        navigate('wait', { roomId: result.roomId });
      } else {
        setError('ルームの作成に失敗しました');
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }
    if (!roomCode.trim() || roomCode.length !== 6) {
      setError('6文字のルームコードを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      // ルーム検索
      const findResult = await findRoomByCode(roomCode.toUpperCase());
      if (!findResult.ok) {
        if (findResult.reason === 'not-found') {
          setError('ルームが見つかりません');
        } else {
          setError('ルームの検索に失敗しました');
        }
        setLoading(false);
        return;
      }

      // ルーム参加
      const joinResult = await joinRoom(findResult.roomId, {
        playerId,
        nickname: inputNickname.trim(),
      });

      if (joinResult.ok) {
        navigate('wait', { roomId: findResult.roomId });
      } else {
        if (joinResult.reason === 'room-full') {
          setError('ルームが満員です');
        } else if (joinResult.reason === 'game-started') {
          setError('ゲームは既に開始されています');
        } else {
          setError('ルームへの参加に失敗しました');
        }
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // メニュー画面（ゲーム選択）
  if (mode === 'menu') {
    return (
      <div className="home-container animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <div className="home-header">
            <h1 className="home-title">
              <NeonText color="pink" size="xl">QUIZ</NeonText>
              <NeonText color="accent" size="xl" style={{ whiteSpace: 'nowrap' }}>GAME</NeonText>
            </h1>
            <p className="home-subtitle">
              <NeonText color="blue" size="sm">ゲームを選んでください</NeonText>
            </p>
          </div>

          <div className="mode-selection">
            {/* ワンワードモード */}
            <GlowingBorder
              color="accent"
              className="mode-card clickable"
              onClick={() => {
                setSelectedGameMode(FL_GAME_MODE.ONEWORD);
                setMode('game-menu');
              }}
            >
              <div className="mode-card-content">
                <h3 className="mode-title">🎯 ワンワード</h3>
                <p className="mode-desc">全員で1文字ずつ答えを完成させる協力クイズ</p>
                <ul className="mode-features">
                  <li>✓ {FIVE_LEAGUE.MIN_PLAYERS}〜{FIVE_LEAGUE.MAX_PLAYERS}人で協力</li>
                  <li>✓ 全員同時に回答</li>
                </ul>
              </div>
            </GlowingBorder>

            {/* チェーンモード（フラグがONの時のみ表示） */}
            {ENABLE_CHAIN_MODE && (
              <GlowingBorder
                color="pink"
                className="mode-card clickable"
                onClick={() => {
                  setSelectedGameMode(FL_GAME_MODE.CHAIN);
                  setMode('game-menu');
                }}
              >
                <div className="mode-card-content">
                  <h3 className="mode-title">⚡ アンサーチェーン</h3>
                  <p className="mode-desc">順番に答えて全問正解を目指す早押しクイズ</p>
                  <ul className="mode-features">
                    <li>✓ {FIVE_LEAGUE.MIN_PLAYERS}〜{FIVE_LEAGUE.MAX_PLAYERS}人で対戦</li>
                    <li>✓ 順番にターン制</li>
                  </ul>
                </div>
              </GlowingBorder>
            )}
          </div>

          <div className="home-buttons secondary-buttons">
            <button
              className="cyber-btn-secondary home-btn how-to-play-btn"
              onClick={() => setShowHowToPlay(true)}
            >
              ? 遊び方
            </button>
            <button
              className="cyber-btn-secondary home-btn account-btn"
              onClick={() => setShowAuth(true)}
            >
              {isEmailUser() ? `👤 ${auth.currentUser?.email?.split('@')[0]}` : '👤 アカウント'}
            </button>
            <button
              className="cyber-btn-secondary home-btn submit-question-btn"
              onClick={() => setShowSubmitQuestion(true)}
            >
              ✏️ 問題を投稿
            </button>
          </div>

          <HowToPlayModal
            isOpen={showHowToPlay}
            onClose={() => setShowHowToPlay(false)}
          />

          <AuthModal
            isOpen={showAuth}
            onClose={() => setShowAuth(false)}
            onAuthChange={handleAuthChange}
          />

          <QuestionSubmitModal
            isOpen={showSubmitQuestion}
            onClose={() => setShowSubmitQuestion(false)}
          />

          <div className="home-footer">
            <AdBanner slot="HOME_BANNER" format="horizontal" />
            <div className="footer-links">
              <button className="footer-link" onClick={() => navigate('privacy')}>
                プライバシーポリシー
              </button>
              <span className="footer-divider">|</span>
              <button className="footer-link" onClick={() => navigate('terms')}>
                利用規約
              </button>
              <span className="footer-divider">|</span>
              <button className="footer-link" onClick={() => navigate('legal')}>
                特商法表記
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ゲームメニュー画面（フリー対戦/ルーム作成/参加）
  if (mode === 'game-menu') {
    const isChainMode = selectedGameMode === FL_GAME_MODE.CHAIN;
    const gameTitle = isChainMode ? '⚡ アンサーチェーン' : '🎯 ワンワード';
    const gameColor = isChainMode ? 'pink' : 'accent';

    return (
      <div className="home-container has-back-btn animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('menu')}>
            ← ゲーム選択
          </button>

          <div className="home-header">
            <h1 className="home-title">
              <NeonText color={gameColor} size="lg">{gameTitle}</NeonText>
            </h1>
          </div>

          {/* チェーンモードの場合、回答数選択 */}
          {isChainMode && (
            <GlowingBorder color="purple" className="chain-options">
              <div className="chain-option-content">
                <label className="option-label">回答数を選択</label>
                <div className="answer-count-buttons">
                  <button
                    className={`answer-count-btn ${selectedAnswerCount === 5 ? 'selected' : ''}`}
                    onClick={() => setSelectedAnswerCount(5)}
                  >
                    5問<br /><span className="time-label">30秒</span>
                  </button>
                  <button
                    className={`answer-count-btn ${selectedAnswerCount === 10 ? 'selected' : ''}`}
                    onClick={() => setSelectedAnswerCount(10)}
                  >
                    10問<br /><span className="time-label">60秒</span>
                  </button>
                </div>
              </div>
            </GlowingBorder>
          )}

          <div className="home-buttons">
            <CyberButton
              variant="accent"
              className="home-btn quick-btn glow-rotate"
              onClick={() => setMode('quick')}
            >
              ⚡ フリー対戦
            </CyberButton>
            <CyberButton
              variant="primary"
              className="home-btn glow-rotate"
              onClick={() => setMode('create')}
            >
              ルームを作る
            </CyberButton>
            <button
              className="cyber-btn-secondary home-btn"
              onClick={() => setMode('join')}
            >
              ルームに参加
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ルーム作成画面
  if (mode === 'create') {
    return (
      <div className="home-container has-back-btn animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('game-menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="pink" size="lg">ルームを作る</NeonText>
          </h2>

          <GlowingBorder color="blue" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="primary"
            className="submit-btn"
            onClick={handleCreateRoom}
            disabled={loading}
          >
            {loading ? '作成中...' : 'ルームを作成'}
          </CyberButton>
        </div>
      </div>
    );
  }

  // ルーム参加画面
  if (mode === 'join') {
    return (
      <div className="home-container has-back-btn animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('game-menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="accent" size="lg">ルームに参加</NeonText>
          </h2>

          <GlowingBorder color="blue" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">ルームコード</label>
              <input
                type="text"
                className="form-input cyber-input room-code-input"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="ABC123"
                maxLength={6}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="primary"
            className="submit-btn"
            onClick={handleJoinRoom}
            disabled={loading}
          >
            {loading ? '参加中...' : 'ルームに参加'}
          </CyberButton>
        </div>
      </div>
    );
  }

  // フリー対戦（クイックマッチ）画面
  if (mode === 'quick') {
    return (
      <div className="home-container has-back-btn animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('game-menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="accent" size="lg">⚡ フリー対戦</NeonText>
          </h2>
          <p className="quick-desc">空いているルームに自動で参加、なければ新規作成します</p>

          <GlowingBorder color="accent" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="accent"
            className="submit-btn"
            onClick={handleQuickMatch}
            disabled={loading}
          >
            {loading ? 'マッチング中...' : '⚡ マッチング開始'}
          </CyberButton>
        </div>
      </div>
    );
  }

  return null;
}

export default Home;
